import React, { useEffect } from "react";
import { Controller } from "react-hook-form";
import Input from "../../shared/components/ui/Input";
import Button from "../../shared/components/ui/Button";
import { Box } from "../../shared/components/ui/chakra";
import AddIcon from "../../shared/components/icons/AddIcon";
import ArrowDropDownIcon from "../../shared/components/icons/ArrowDropDownIcon";
import SearchIcon from "../../shared/components/icons/SearchIcon";
import useSearchForm from "../lib/useSearchForm";
import CurrentSearchCondition from "./CurrentSearchCondition";
import DetailSearchForm from "./DetailSearchForm";


import {
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Show,
  useDisclosure,
} from "@chakra-ui/react";

type SearchFormProps = {
  tags: Array<{ id: number; name: string }>;
};

const SearchForm = ({ tags }: SearchFormProps) => {
  const { control, handleSubmit, setValue, watch } = useSearchForm();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setValue("keyword", params.get("q[title_or_content]") ?? "");
    setValue("tags", params.getAll("q[tags_id_eq_any][]") ?? []);
    setValue("only_bookmarked", params.get("only_bookmarked") ?? "0");
  }, [setValue]);

  const onSubmit = (data: {
    keyword: string;
    tags: string[];
    only_bookmarked: string;
  }) => {
    const url = new URL(location.href);
    url.searchParams.delete("q[title_or_content]");
    url.searchParams.delete("q[tags_id_eq_any][]");
    url.searchParams.delete("only_bookmarked");
    url.searchParams.delete("search");
    url.searchParams.delete("page");

    url.searchParams.append("q[title_or_content]", data.keyword);
    url.searchParams.append("only_bookmarked", data.only_bookmarked);

    data.tags.forEach((tag) => {
      url.searchParams.append("q[tags_id_eq_any][]", tag);
    });
    url.searchParams.append("search", "1");

    location.href = url.toString();
  };

  const [watchTags, watchOnlyBookmarked] = watch(["tags", "only_bookmarked"]);

  const {
    isOpen: isOpenPC,
    onToggle: onTogglePC,
    onClose: onClosePC,
  } = useDisclosure();
  const {
    isOpen: isOpenSP,
    onToggle: onToggleSP,
    onClose: onCloseSP,
  } = useDisclosure();

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="keyword"
        control={control}
        render={({ field }) => (
          <>
            <Popover isOpen={isOpenPC} onClose={onClosePC} matchWidth>
              <PopoverTrigger>
                <Flex gap={0} alignItems="stretch">
                  <Input
                    placeholder="気になるキーワードを入力"
                    borderRadius={4}
                    h="full"
                    roundedRight="none"
                    {...field}
                  />
                  <Show above="md">
                    <Box
                      as="button"
                      pl={5}
                      pr={6}
                      color="primary"
                      wordBreak="keep-all"
                      fontSize="xs"
                      fontWeight="bold"
                      display="flex"
                      gap={0.5}
                      bg="white"
                      borderWidth={"1px"}
                      borderColor="border.input"
                      alignItems="center"
                      type="button"
                      onClick={onTogglePC}
                    >
                      <ArrowDropDownIcon />
                      <Box as="span" mt={0.5}>
                        詳細検索
                      </Box>
                    </Box>
                  </Show>
                  <Box h="full">
                    <Button
                      boxSize={12}
                      roundedLeft="none"
                      type="submit"
                      aria-label="検索する"
                    >
                      <SearchIcon />
                    </Button>
                  </Box>
                </Flex>
              </PopoverTrigger>
              <PopoverContent
                w="full"
                pt={4}
                pb={5}
                px={5}
                borderColor="border.input"
              >
                <PopoverBody>
                  <DetailSearchForm tags={tags} methods={{ control }} />
                </PopoverBody>
              </PopoverContent>
            </Popover>
            <Show below="md">
              <Popover isOpen={isOpenSP} onClose={onCloseSP} matchWidth>
                <PopoverTrigger>
                  <Box w="full">
                    <Button
                      mt={1}
                      variant="outline"
                      borderWidth={0}
                      fontSize="xs"
                      borderRadius={2}
                      py={1}
                      pl={1}
                      pr={2}
                      onClick={onToggleSP}
                    >
                      <Flex gap={0.5}>
                        <AddIcon boxSize={4} />
                        <Box as="span">詳細検索</Box>
                      </Flex>
                    </Button>
                  </Box>
                </PopoverTrigger>
                <PopoverContent
                  w="full"
                  pt={4}
                  pb={5}
                  px={4}
                  borderColor="border.input"
                >
                  <PopoverBody>
                    <DetailSearchForm tags={tags} methods={{ control }} />
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Show>
          </>
        )}
      />
      <CurrentSearchCondition
        watchTags={watchTags}
        watchOnlyBookmarked={watchOnlyBookmarked}
        tags={tags}
      />
    </Box>
  );
};

export default SearchForm;
